import { useEffect, useRef, useState, useCallback } from "react";
import { formatAttendanceDate } from "../utils/utils";
import { LMSBASEURL } from "../config/apiUrls";

const getToday = () => new Date().toISOString().split("T")[0];

export function useActiveTimeTracker(sendTimeToBackend, userId, role) {
  const storageKey = `activeTimeData:${userId}:${role}`;
  const [seconds, setSeconds] = useState(0);
  const [isTracking, setIsTracking] = useState(false);

  const visibleRef = useRef(document.visibilityState === "visible");
  const tickRef = useRef(null);
  const bcRef = useRef(null);

  const start = useCallback(
    (initialSeconds = 0) => {
      if (!userId || role !== "Student") return;
      if (isTracking) return;
      setSeconds(initialSeconds);
      persist(initialSeconds);
      setIsTracking(true);
    },
    [userId, role, isTracking]
  );

  const persist = (s) => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({ date: getToday(), time: s })
    );
  };

  const broadcast = (s) => {
    if (bcRef.current) {
      bcRef.current.postMessage({
        type: "SYNC",
        time: s,
        date: getToday(),
        userId,
        role,
      });
    }
  };

  // accrues +1 second every tick while visible
  const accrueDelta = useCallback(() => {
    if (!visibleRef.current) return;
    setSeconds((prev) => {
      const next = prev + 1;
      persist(next);
      broadcast(next);
      return next;
    });
  }, []);

  const sendToBackend = () => {
    const total =
      JSON.parse(localStorage.getItem(storageKey) || "{}")?.time || 0;
    if (total > 0) {
      const payload = JSON.stringify({
        student_id: userId,
        attendance_date: formatAttendanceDate(),
        status: "present",
        remarks: "Attended on time",
        time_spend: total,
      });
      const APItoken = process.env.REACT_APP_API_TOKEN;

      fetch(`${LMSBASEURL}/api/student-attendance`, {
        method: "POST",
        headers: {
          Authorization: APItoken,
          "Content-Type": "application/json",
        },
        body: payload,
        keepalive: true,
      })
        .then(() => {
          localStorage.removeItem(storageKey);
        })
        .catch((error) => {
          console.error("Failed to send attendance:", error);
        });
    }
  };

  const flushTimeData = () => {
    sendToBackend();
  };

  useEffect(() => {
    if (!userId || role !== "Student") return;
    if (!isTracking) return;
    sessionStorage.setItem(
      "activeTimeIdentity",
      JSON.stringify({ userId, role })
    );

    bcRef.current = new BroadcastChannel("active-time");
    bcRef.current.onmessage = (e) => {
      if (e.data?.type === "SYNC" && e.data.userId === userId && e.data.role === role) {
        const { time, date } = e.data;
        if (date === getToday()) {
          setSeconds(time);
          persist(time);
        }
      }
    };

    const onVisibilityChange = () => {
      visibleRef.current = document.visibilityState === "visible";
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const onBeforeUnload = () => {
      sendToBackend();
    };

    const onPageHide = () => {
      sendToBackend();
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);

    tickRef.current = setInterval(accrueDelta, 1000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);

      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      if (bcRef.current) {
        bcRef.current.close();
        bcRef.current = null;
      }
    };
  }, [userId, role,isTracking, accrueDelta]);

  return {
    seconds,
    flushTimeData,
    start
  };
}

export default useActiveTimeTracker;
